/** @jsx jsx */

import { Fragment, useMemo } from 'react';
import { format } from 'date-fns';
import { jsx } from '@balance-web/core';
import { ChevronDownIcon } from '@balance-web/icon/icons/ChevronDownIcon';
import { ChevronUpIcon } from '@balance-web/icon/icons/ChevronUpIcon';
import type { BalanceThemeRaw } from '@balance-web/theme';
import { useRawTheme } from '@balance-web/theme';

import type { CalendarPeriod } from './calendarPeriods';

type CalendarCaptionButtonsProps = {
  month: Date;
  expandedPeriod?: CalendarPeriod;
  onPeriodClick: (period: CalendarPeriod, trigger: HTMLButtonElement) => void;
};

export const CalendarCaptionButtons = ({
  month,
  expandedPeriod,
  onPeriodClick,
}: CalendarCaptionButtonsProps) => {
  const theme = useRawTheme();
  const buttonStyles = useMemo(() => getCaptionButtonStyles(theme), [theme]);

  return (
    <Fragment>
      <CaptionButton
        buttonStyles={buttonStyles}
        isExpanded={expandedPeriod === 'month'}
        label={format(month, 'MMMM')}
        period="month"
        onPeriodClick={onPeriodClick}
      />{' '}
      <CaptionButton
        buttonStyles={buttonStyles}
        isExpanded={expandedPeriod === 'year'}
        label={format(month, 'yyyy')}
        period="year"
        onPeriodClick={onPeriodClick}
      />
    </Fragment>
  );
};

type CaptionButtonProps = {
  buttonStyles: ReturnType<typeof getCaptionButtonStyles>;
  isExpanded: boolean;
  label: string;
  period: CalendarPeriod;
  onPeriodClick: (period: CalendarPeriod, trigger: HTMLButtonElement) => void;
};

const CaptionButton = ({
  buttonStyles,
  isExpanded,
  label,
  period,
  onPeriodClick,
}: CaptionButtonProps) => {
  const Chevron = isExpanded ? ChevronUpIcon : ChevronDownIcon;

  return (
    <button
      aria-expanded={isExpanded}
      aria-haspopup="dialog"
      css={buttonStyles}
      data-calendar-period={period}
      type="button"
      onClick={(event) => {
        onPeriodClick(period, event.currentTarget);
      }}
    >
      {label}
      <Chevron size="small" />
    </button>
  );
};

function getCaptionButtonStyles(theme: BalanceThemeRaw) {
  const { colour } = theme.semantic;
  const interactions = colour.neutral.container.interactions;
  const overlay = (interactionColour: string) =>
    `linear-gradient(${interactionColour}, ${interactionColour})`;

  return {
    alignItems: 'center',
    appearance: 'none',
    backgroundColor: colour.surface.neutral.background2,
    border: 0,
    borderRadius: theme.radii.small,
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    gap: theme.spacing.xsmall,
    height: theme.sizing.small,
    margin: 0,
    outline: 0,
    padding: `0 ${theme.spacing.xsmall}px 0 ${theme.spacing.small}px`,

    ':hover': {
      backgroundImage: overlay(interactions.hover),
    },
    ':focus-visible': {
      backgroundImage: overlay(interactions.focused),
      boxShadow: `inset 0 0 0 2px ${colour.primary.background}`,
    },
    ':active, &[aria-expanded="true"]': {
      backgroundImage: overlay(interactions.pressed),
    },
  } as const;
}
